import { useState, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Mic, MicOff, Loader2, Square, Volume2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Universal Voice Input Component
 * Provides voice-to-text capabilities across the application
 * 
 * @param {function} onTranscription - Callback when transcription is complete
 * @param {string} context - Transcription context (clinical_note, sbar_call, prescription, etc.)
 * @param {boolean} enhanced - Whether to use AI enhancement
 * @param {string} outputFormat - Format for enhanced output (raw, structured, soap_note, bullet_points)
 * @param {string} patientId - Optional patient ID for context
 * @param {string} patientName - Optional patient name for context
 * @param {string} className - Additional CSS classes
 * @param {boolean} showBadge - Show recording status badge
 * @param {string} buttonVariant - Button variant (default, outline, ghost)
 * @param {string} size - Button size (default, sm, lg, icon)
 */
export default function VoiceInput({
  onTranscription,
  context = "general",
  enhanced = false,
  outputFormat = "raw",
  patientId = null,
  patientName = null,
  className = "",
  showBadge = true,
  buttonVariant = "outline",
  size = "default",
  disabled = false,
  placeholder = "Click to record"
}) {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [duration, setDuration] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Use webm for better compatibility
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') 
        ? 'audio/webm' 
        : 'audio/mp4';
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        clearInterval(timerRef.current);
        
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        await processAudio(audioBlob);
      };

      mediaRecorder.start(100); // Collect data every 100ms
      setIsRecording(true);
      setDuration(0);

      // Start timer
      timerRef.current = setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);

      toast.success("Recording started");
    } catch (err) {
      console.error("Failed to start recording:", err);
      toast.error("Microphone access denied. Please enable microphone permissions.");
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(timerRef.current);
    }
  }, [isRecording]);

  const processAudio = async (audioBlob) => {
    setIsProcessing(true);

    try {
      // Convert to base64
      const reader = new FileReader();
      const base64Promise = new Promise((resolve, reject) => {
        reader.onloadend = () => {
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
      });
      reader.readAsDataURL(audioBlob);
      const audioBase64 = await base64Promise;

      // Choose endpoint based on enhanced flag
      const endpoint = enhanced 
        ? `${API_URL}/api/voice/transcribe-enhanced`
        : `${API_URL}/api/voice/transcribe`;

      const body = enhanced
        ? {
            audio_base64: audioBase64,
            context,
            output_format: outputFormat,
            patient_id: patientId,
            patient_name: patientName,
            include_summary: true
          }
        : {
            audio_base64: audioBase64,
            context,
            language: "en",
            patient_id: patientId,
            patient_name: patientName
          };

      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        throw new Error(`Transcription failed: ${response.status}`);
      }

      const data = await response.json();
      
      // Call the callback with transcription result
      if (onTranscription) {
        onTranscription({
          text: enhanced ? data.enhanced_text : data.text,
          originalText: data.original_text || data.text,
          summary: data.summary,
          duration: data.duration_seconds,
          wordCount: data.word_count,
          context,
          enhanced
        });
      }

      toast.success("Transcription complete");
    } catch (err) {
      console.error("Transcription error:", err);
      toast.error("Failed to transcribe audio");
    } finally {
      setIsProcessing(false);
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleClick = () => {
    if (isRecording) {
      stopRecording();
    } else if (!isProcessing && !disabled) {
      startRecording();
    }
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Button
        variant={isRecording ? "destructive" : buttonVariant}
        size={size}
        onClick={handleClick}
        disabled={isProcessing || disabled}
        data-testid="voice-input-btn"
        className={isRecording ? "animate-pulse" : ""}
      >
        {isProcessing ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Processing...
          </>
        ) : isRecording ? (
          <>
            <Square className="w-4 h-4 mr-2" />
            Stop ({formatDuration(duration)})
          </>
        ) : (
          <>
            <Mic className="w-4 h-4 mr-2" />
            {placeholder}
          </>
        )}
      </Button>

      {showBadge && isRecording && (
        <Badge variant="destructive" className="animate-pulse">
          <Volume2 className="w-3 h-3 mr-1" />
          Recording
        </Badge>
      )}
    </div>
  );
}

/**
 * Voice Input Button (Icon only variant)
 */
export function VoiceInputIcon({
  onTranscription,
  context = "general",
  enhanced = false,
  outputFormat = "raw",
  patientId = null,
  patientName = null,
  className = "",
  disabled = false,
  title = "Record voice"
}) {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') 
        ? 'audio/webm' 
        : 'audio/mp4';
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        await processAudio(audioBlob);
      };

      mediaRecorder.start(100);
      setIsRecording(true);
      toast.success("Recording...");
    } catch (err) {
      toast.error("Microphone access denied");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const processAudio = async (audioBlob) => {
    setIsProcessing(true);
    try {
      const reader = new FileReader();
      const base64Promise = new Promise((resolve) => {
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
      });
      reader.readAsDataURL(audioBlob);
      const audioBase64 = await base64Promise;

      const endpoint = enhanced 
        ? `${API_URL}/api/voice/transcribe-enhanced`
        : `${API_URL}/api/voice/transcribe`;

      const body = enhanced
        ? { audio_base64: audioBase64, context, output_format: outputFormat, patient_id: patientId, patient_name: patientName }
        : { audio_base64: audioBase64, context, language: "en", patient_id: patientId, patient_name: patientName };

      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });

      if (!response.ok) throw new Error("Transcription failed");
      const data = await response.json();
      
      if (onTranscription) {
        onTranscription({
          text: enhanced ? data.enhanced_text : data.text,
          originalText: data.original_text || data.text,
          summary: data.summary,
          context,
          enhanced
        });
      }
      toast.success("Done");
    } catch (err) {
      toast.error("Transcription failed");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Button
      variant={isRecording ? "destructive" : "ghost"}
      size="icon"
      onClick={isRecording ? stopRecording : startRecording}
      disabled={isProcessing || disabled}
      title={title}
      className={`${className} ${isRecording ? "animate-pulse" : ""}`}
      data-testid="voice-input-icon"
    >
      {isProcessing ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : isRecording ? (
        <MicOff className="w-4 h-4" />
      ) : (
        <Mic className="w-4 h-4" />
      )}
    </Button>
  );
}
